import { createAction, createReducer } from "@reduxjs/toolkit";

import type { RootState } from "~/store";

export type PrescriptionMeta = {
  ready_for_modeling: boolean;
  filled_count: number;
  total_fields: number;
  completion_percent: number;
  blocking_questions: string[];
  recommended_to_clarify: string[];
  assumptions_made: string[];
  low_confidence_fields: string[];
};

export type SmartPrescriptionState = {
  operation: "audio" | "recipe-update";
  /** the doctor is actively recording (including background auto-segment takes) — while
   *  true, the processing modal/pipeline must stay out of the way entirely; only a
   *  manual stop should ever let it engage. */
  isRecording: boolean;
  /** the stopped recording is being prepared and uploaded before transcription. */
  isUploading: boolean;
  /** the audio upload failed — surfaced in the modal so the doctor can retry. */
  uploadError: string | null;
  micError: string | null;
  /** grok is transcribing or processing; the recipe form is locked. */
  isProcessing: boolean;
  /** the model has filled the recipe at least once — the form is unlocked for review. */
  hasBeenFilled: boolean;
  meta: PrescriptionMeta | null;
  error: string | null;
  /** transcript that produced the current fill, to avoid re-running on the same text. */
  lastTranscript: string | null;
};

const initialState: SmartPrescriptionState = {
  operation: "audio",
  isRecording: false,
  isUploading: false,
  uploadError: null,
  micError: null,
  isProcessing: false,
  hasBeenFilled: false,
  meta: null,
  error: null,
  lastTranscript: null,
};

export const smartPrescriptionRecordingStarted = createAction("SMART_RX_RECORDING_STARTED");
export const smartPrescriptionRecordingStopped = createAction("SMART_RX_RECORDING_STOPPED");
export const smartPrescriptionUploadStarted = createAction("SMART_RX_UPLOAD_STARTED");
export const smartPrescriptionUploadFinished = createAction("SMART_RX_UPLOAD_FINISHED");
export const smartPrescriptionUploadFailed = createAction<string>("SMART_RX_UPLOAD_FAILED");
export const smartPrescriptionMicBlocked = createAction<string>("SMART_RX_MIC_BLOCKED");
export const smartPrescriptionRecipeUpdateStarted = createAction("SMART_RX_RECIPE_UPDATE_STARTED");
export const smartPrescriptionProcessingStarted = createAction<string>("SMART_RX_PROCESSING_STARTED");
export const smartPrescriptionFilled = createAction<{ meta: PrescriptionMeta | null }>("SMART_RX_FILLED");
export const smartPrescriptionFailed = createAction<string>("SMART_RX_FAILED");
export const smartPrescriptionReset = createAction("SMART_RX_RESET");

export const selectSmartPrescription = (state: RootState): SmartPrescriptionState => state.smartPrescription;

export const smartPrescription = createReducer(initialState, (builder) =>
  builder
    .addCase(smartPrescriptionRecordingStarted, (state) => {
      state.operation = "audio";
      state.isRecording = true;
      state.micError = null;
    })
    .addCase(smartPrescriptionRecordingStopped, (state) => {
      state.isRecording = false;
    })
    .addCase(smartPrescriptionUploadStarted, (state) => {
      state.operation = "audio";
      state.isUploading = true;
      state.uploadError = null;
    })
    .addCase(smartPrescriptionUploadFinished, (state) => {
      state.isUploading = false;
    })
    .addCase(smartPrescriptionUploadFailed, (state, action) => {
      state.isUploading = false;
      state.uploadError = action.payload;
    })
    .addCase(smartPrescriptionMicBlocked, (state, action) => {
      state.isRecording = false;
      state.micError = action.payload;
    })
    .addCase(smartPrescriptionRecipeUpdateStarted, (state) => {
      state.operation = "recipe-update";
    })
    .addCase(smartPrescriptionProcessingStarted, (state, action) => {
      state.isProcessing = true;
      state.error = null;
      state.lastTranscript = action.payload;
    })
    .addCase(smartPrescriptionFilled, (state, action) => {
      state.isProcessing = false;
      state.hasBeenFilled = true;
      state.meta = action.payload.meta;
      state.error = null;
    })
    .addCase(smartPrescriptionFailed, (state, action) => {
      state.isProcessing = false;
      state.error = action.payload;
    })
    .addCase(smartPrescriptionReset, () => initialState),
);
